import React from 'react';

import { Button } from '@/components/ui/button';

export type MedcallParticipantActionKey =
  | 'add-medication'
  | 'add-to-my-clients'
  | 'remove-from-my-clients'
  | 'assign-staff'
  | 'assign-nurse'
  | 'assign-psychiatrist'
  | 'participant-status'
  | 'report'
  | 'med-list'
  | 'edit-client'
  | 'client-aliases'
  | 'client-notes'
  | 'medcall-message';

type VisibilityContext = {
  canManageMedications: boolean;
  canManageParticipant: boolean;
  isNurselessProgram: boolean;
  isOnMyClients: boolean;
};

export const getMedcallParticipantActionOrder = ({
  canManageMedications,
  canManageParticipant,
  isNurselessProgram,
  isOnMyClients,
}: VisibilityContext): MedcallParticipantActionKey[] => {
  const actions: MedcallParticipantActionKey[] = [];
  if (canManageMedications) {
    actions.push('add-medication');
  }

  actions.push(isOnMyClients ? 'remove-from-my-clients' : 'add-to-my-clients');
  if (canManageParticipant) {
    actions.push('assign-staff');
  }

  if (!isNurselessProgram) {
    actions.push('assign-nurse');
  }

  if (canManageParticipant) {
    actions.push('assign-psychiatrist', 'participant-status');
  }

  if (!isNurselessProgram) {
    actions.push('report');
  }

  actions.push('med-list');
  if (canManageParticipant) {
    actions.push('edit-client', 'client-aliases');
  }

  actions.push('client-notes', 'medcall-message');
  return actions;
};

type MedcallParticipantActionsProps = VisibilityContext & {
  renderParticipantStatusAction?: (button: React.ReactElement) => React.ReactNode;
  participantActive: boolean;
  pending: boolean;
  onAddMedication: () => void;
  onAddToMyClients: () => void;
  onAssignNurse: () => void;
  onAssignPsychiatrist: () => void;
  onAssignStaff: () => void;
  onClientAliases: () => void;
  onClientNotes: () => void;
  onEditClient: () => void;
  onMedcallMessage: () => void;
  onMedicationList: () => void;
  onParticipantStatus: () => void;
  onRemoveFromMyClients: () => void;
  onReport: () => void;
};

const ACTION_PRESENTATION: Record<MedcallParticipantActionKey, { className: string; label: string }> = {
  'add-medication': {
    className: 'medcall-action-button--add-medication',
    label: 'Add Medication',
  },
  'add-to-my-clients': {
    className: 'medcall-action-button--my-clients',
    label: 'Add to My Clients',
  },
  'remove-from-my-clients': {
    className: 'medcall-action-button--my-clients',
    label: 'Remove From My Clients',
  },
  'assign-staff': {
    className: 'medcall-action-button--staff',
    label: 'Assign Staff',
  },
  'assign-nurse': {
    className: 'medcall-action-button--nurse',
    label: 'Assign Nurse',
  },
  'assign-psychiatrist': {
    className: 'medcall-action-button--psychiatrist',
    label: 'Assign Psychiatrist',
  },
  'participant-status': {
    className: 'medcall-action-button--status',
    label: 'Participant Status',
  },
  report: {
    className: 'medcall-action-button--report',
    label: 'Report',
  },
  'med-list': {
    className: 'medcall-action-button--med-list',
    label: 'Med List',
  },
  'edit-client': {
    className: 'medcall-action-button--edit',
    label: 'Edit Client',
  },
  'client-aliases': {
    className: 'medcall-action-button--edit',
    label: 'Client Aliases',
  },
  'client-notes': {
    className: 'medcall-action-button--edit',
    label: 'Client Notes',
  },
  'medcall-message': {
    className: 'medcall-action-button--message',
    label: 'MedCall Message',
  },
};

export const MedcallParticipantActions = ({
  canManageMedications,
  canManageParticipant,
  isNurselessProgram,
  isOnMyClients,
  onAddMedication,
  onAddToMyClients,
  onAssignNurse,
  onAssignPsychiatrist,
  onAssignStaff,
  onClientAliases,
  onClientNotes,
  onEditClient,
  onMedcallMessage,
  onMedicationList,
  onParticipantStatus,
  onRemoveFromMyClients,
  onReport,
  participantActive,
  pending,
  renderParticipantStatusAction,
}: MedcallParticipantActionsProps) => {
  const actions = getMedcallParticipantActionOrder({
    canManageMedications,
    canManageParticipant,
    isNurselessProgram,
    isOnMyClients,
  });
  const handlers: Record<MedcallParticipantActionKey, () => void> = {
    'add-medication': onAddMedication,
    'add-to-my-clients': onAddToMyClients,
    'remove-from-my-clients': onRemoveFromMyClients,
    'assign-staff': onAssignStaff,
    'assign-nurse': onAssignNurse,
    'assign-psychiatrist': onAssignPsychiatrist,
    'participant-status': onParticipantStatus,
    report: onReport,
    'med-list': onMedicationList,
    'edit-client': onEditClient,
    'client-aliases': onClientAliases,
    'client-notes': onClientNotes,
    'medcall-message': onMedcallMessage,
  };

  return (
    <div className="medcall-participant-actions" data-testid="medcall-participant-actions">
      {actions.map((action) => {
        const presentation = ACTION_PRESENTATION[action];
        const label = action === 'participant-status'
          ? (participantActive ? 'Deactivate Client' : 'Activate Client')
          : presentation.label;
        const button = (
          <Button
            key={action}
            className={`medcall-action-button ${presentation.className}`}
            data-medcall-action={action}
            disabled={pending}
            onClick={handlers[action]}
            size="sm"
            type="button"
            variant="outline"
          >
            {label}
          </Button>
        );
        if (action === 'participant-status' && renderParticipantStatusAction) {
          return <React.Fragment key={action}>{renderParticipantStatusAction(button)}</React.Fragment>;
        }
        return button;
      })}
    </div>
  );
};
